import { useState, useEffect, useCallback } from "react";
import { Moon, Sun } from "lucide-react";
import "./App.css";
import CreateUserGroup from "./CreateUserGroup";
import AccessSearch from "./AccessSearch";
import AccessRequestHistory from "./AccessRequestHistory";
import SecurityAccessReport from "./SecurityAccessReport";
import SecurityFieldTesting from "./SecurityFieldTesting";
import ServiceNowActions from "./ServiceNowActions";
import SprintMetrics from "./SprintMetrics";
import EntitlementLookup from "./EntitlementLookup";
import FeedbackInbox from "./FeedbackInbox";
import AnalyticsDashboard from "./AnalyticsDashboard";
import analytics from "./analytics";
import ProductionWorkspaceHome from "./ProductionWorkspaceHome";
import ResourceSidebar, { NavigationToggle } from "./ResourceSidebar";
import { resourceTools } from "./ResourceTools";
import ToolIcon from "./ToolIcon";
import { canAccessPage, groupAccessEnabled } from "./groupAccess";

// ── CompareAccess constants ────────────────────────────────────────────────
const CA_MAX_USERS = 5;
const CA_MIN_USERS = 2;
const CA_USER_COLORS = [
  { accent: "var(--green)",  dim: "var(--green-dim)",  border: "#1a4a28" },
  { accent: "var(--blue)",   dim: "var(--blue-dim)",   border: "#1a3a5c" },
  { accent: "var(--amber)",  dim: "var(--amber-dim)",  border: "#4a3600" },
  { accent: "#b57bee",       dim: "#1e0a38",           border: "#4a2278" },
  { accent: "#f07070",       dim: "#2d0f0f",           border: "#5a1a1a" },
];

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";
const FEEDBACK_ADMIN_EMAILS = new Set((process.env.REACT_APP_FEEDBACK_ADMIN_EMAILS || "")
  .split(",")
  .map((email) => email.trim().toLowerCase())
  .filter(Boolean));
const IS_AUTH_PREVIEW = process.env.NODE_ENV !== "production"
  && new URLSearchParams(window.location.search).has("auth-preview");

async function apiFetch(path, retries = 2) {
  const sep = path.includes("?") ? "&" : "?";
  const res = await fetch(`${API_BASE}${path}${sep}_=${Date.now()}`);
  if (!res.ok) {
    const text = await res.text();
    // Retry on rate limit errors
    if (res.status === 429 && retries > 0) {
      await new Promise(r => setTimeout(r, 8000));
      return apiFetch(path, retries - 1);
    }
    throw new Error(`API error ${res.status}: ${text}`);
  }
  return res.json();
}


// ── Generic artifact diff (Data Access Sets) ───────────────────────────────
function diffArtifacts(prodList, draftList, nameKey = "displayName") {
  const prodMap = Object.fromEntries(prodList.map(i => [i[nameKey], i]));
  const draftMap = Object.fromEntries(draftList.map(i => [i[nameKey], i]));
  const allNames = [...new Set([...Object.keys(prodMap), ...Object.keys(draftMap)])];
  return allNames.map(name => {
    const prod = prodMap[name] || null;
    const draft = draftMap[name] || null;
    let status, diffKeys = [];
    if (prod && draft) {
      const allKeys = [...new Set([...Object.keys(prod), ...Object.keys(draft)])];
      diffKeys = allKeys.filter(k => JSON.stringify(prod[k]) !== JSON.stringify(draft[k]));
      status = diffKeys.length === 0 ? "match" : "mismatch";
    } else if (prod) { status = "prod_only"; }
    else { status = "draft_only"; }
    return { name, prod, draft, status, diffKeys };
  }).sort((a, b) => {
    const order = { mismatch: 0, prod_only: 1, draft_only: 2, match: 3 };
    const sd = order[a.status] - order[b.status];
    return sd !== 0 ? sd : a.name.localeCompare(b.name);
  });
}

// ── User group capability diff ─────────────────────────────────────────────
function diffUserGroups(prodGroups, draftGroups) {
  const prodMap = Object.fromEntries(prodGroups.map(g => [g.displayName, g]));
  const draftMap = Object.fromEntries(draftGroups.map(g => [g.displayName, g]));
  const allNames = [...new Set([...Object.keys(prodMap), ...Object.keys(draftMap)])];

  return allNames.map(name => {
    const prod = prodMap[name] || null;
    const draft = draftMap[name] || null;
    let status = "match";
    let capDiffs = [];

    if (prod && draft) {
      const prodCaps = new Set(prod.capabilities || []);
      const draftCaps = new Set(draft.capabilities || []);
      const allCaps = [...new Set([...prodCaps, ...draftCaps])].sort();

      capDiffs = allCaps.map(cap => {
        const inProd = prodCaps.has(cap);
        const inDraft = draftCaps.has(cap);
        let capStatus = "match";
        if (inProd && !inDraft) capStatus = "prod_only";
        else if (!inProd && inDraft) capStatus = "draft_only";
        return { cap, inProd, inDraft, status: capStatus };
      });

      status = capDiffs.some(c => c.status !== "match") ? "mismatch" : "match";
    } else if (prod) {
      status = "prod_only";
      capDiffs = (prod.capabilities || []).map(cap => ({ cap, inProd: true, inDraft: false, status: "prod_only" }));
    } else {
      status = "draft_only";
      capDiffs = (draft.capabilities || []).map(cap => ({ cap, inProd: false, inDraft: true, status: "draft_only" }));
    }

    const changedCount = capDiffs.filter(c => c.status !== "match").length;
    return { name, prod, draft, status, capDiffs, changedCount };
  }).sort((a, b) => {
    const order = { mismatch: 0, prod_only: 1, draft_only: 2, match: 3 };
    const sd = order[a.status] - order[b.status];
    return sd !== 0 ? sd : a.name.localeCompare(b.name);
  });
}

function diffContentPackages(prodList, draftList) {
  const prodMap = Object.fromEntries(prodList.map(item => [item.displayName, item]));
  const draftMap = Object.fromEntries(draftList.map(item => [item.displayName, item]));
  const names = [...new Set([...Object.keys(prodMap), ...Object.keys(draftMap)])];
  const visibleArtifacts = (artifactType, artifacts) => artifacts.filter(artifact => (
    artifactType === "keyGroup"
      ? artifact.canFilterBy === true || artifact.canGroupBy === true
      : artifactType === "guidebookTopic" || artifact.visible === true
  ));
  const artifactKey = artifact => artifact.objectName || artifact.uuid || artifact.displayName || JSON.stringify(artifact);

  return names.map(name => {
    const prod = prodMap[name] || null;
    const draft = draftMap[name] || null;
    let status = "match";
    let artifactTypeDiffs = [];

    if (!prod || !draft) {
      status = prod ? "prod_only" : "draft_only";
      const packageValue = prod || draft;
      const presentSide = prod ? "prod" : "draft";
      artifactTypeDiffs = (packageValue.artifactsByType || []).map(type => {
        const artifacts = visibleArtifacts(type.artifactType, type.artifacts || []);
        return artifacts.length ? {
          artifactType: type.artifactType,
          status,
          artifactDiffs: artifacts.map(artifact => ({
            key: artifactKey(artifact),
            prod: presentSide === "prod" ? artifact : null,
            draft: presentSide === "draft" ? artifact : null,
            status,
          })),
        } : null;
      }).filter(Boolean);
    } else {
      const prodTypes = prod.artifactsByType || [];
      const draftTypes = draft.artifactsByType || [];
      const prodTypeMap = Object.fromEntries(prodTypes.map(type => [type.artifactType, type.artifacts || []]));
      const draftTypeMap = Object.fromEntries(draftTypes.map(type => [type.artifactType, type.artifacts || []]));
      const types = [...new Set([...Object.keys(prodTypeMap), ...Object.keys(draftTypeMap)])];

      artifactTypeDiffs = types.map(artifactType => {
        const prodArtifacts = visibleArtifacts(artifactType, prodTypeMap[artifactType] || []);
        const draftArtifacts = visibleArtifacts(artifactType, draftTypeMap[artifactType] || []);
        const prodByKey = Object.fromEntries(prodArtifacts.map(artifact => [artifactKey(artifact), artifact]));
        const draftByKey = Object.fromEntries(draftArtifacts.map(artifact => [artifactKey(artifact), artifact]));
        const keys = [...new Set([...Object.keys(prodByKey), ...Object.keys(draftByKey)])];
        const artifactDiffs = keys.map(key => {
          const prodArtifact = prodByKey[key] || null;
          const draftArtifact = draftByKey[key] || null;
          let artifactStatus = "match";
          if (prodArtifact && !draftArtifact) artifactStatus = "prod_only";
          else if (!prodArtifact && draftArtifact) artifactStatus = "draft_only";
          else if (artifactType === "keyGroup") {
            if (prodArtifact.canFilterBy !== draftArtifact.canFilterBy || prodArtifact.canGroupBy !== draftArtifact.canGroupBy) {
              artifactStatus = "mismatch";
            }
          } else if (artifactType !== "guidebookTopic" && prodArtifact.visible !== draftArtifact.visible) {
            artifactStatus = "mismatch";
          }
          return { key, prod: prodArtifact, draft: draftArtifact, status: artifactStatus };
        }).filter(artifact => artifact.status !== "match");

        return artifactDiffs.length ? {
          artifactType,
          artifactDiffs,
          status: "mismatch",
        } : null;
      }).filter(Boolean);
      status = artifactTypeDiffs.length ? "mismatch" : "match";
    }

    return { name, prod, draft, status, artifactTypeDiffs, diffCount: artifactTypeDiffs.length };
  }).sort((left, right) => {
    const order = { mismatch: 0, prod_only: 1, draft_only: 2, match: 3 };
    return (order[left.status] - order[right.status]) || left.name.localeCompare(right.name);
  });
}

// ── Permission deep diff ───────────────────────────────────────────────────
function getDimId(df) {
  if (df.staticDimensionFilter)  return df.staticDimensionFilter.dimensionId;
  if (df.dynamicDimensionFilter) return df.dynamicDimensionFilter.dimensionId;
  return JSON.stringify(df);
}

function flattenDimFilters(dataSecurityProfile) {
  const map = {};
  if (!dataSecurityProfile?.memberFilterConfigs) return map;
  for (const mfc of dataSecurityProfile.memberFilterConfigs) {
    for (const df of mfc.dimensionFilters || []) {
      const id = getDimId(df);
      map[id] = df;
    }
  }
  return map;
}

function getMemberSummary(df) {
  if (!df) return "—";
  const filter = df.staticDimensionFilter || df.dynamicDimensionFilter;
  if (!filter) return "—";
  if (filter.memberSelections) {
    const selections = filter.memberSelections.filter(m => m.namePath);
    const paths = selections.slice(0, 5).map(m => `${m.excluded ? "excl: " : ""}${m.namePath.join("/")}`);
    const extra = selections.length > 5 ? ` +${selections.length - 5} more` : "";
    return paths.length > 0 ? paths.join(", ") + extra : "all members";
  }
  if (filter.dynamicPropertyMappings) return filter.dynamicPropertyMappings.map(m => m.hierarchyPropertyId).join(", ");
  return "configured";
}

function diffPermissions(prodList, draftList) {
  const prodMap = Object.fromEntries(prodList.map(i => [i.displayName, i]));
  const draftMap = Object.fromEntries(draftList.map(i => [i.displayName, i]));
  const allNames = [...new Set([...Object.keys(prodMap), ...Object.keys(draftMap)])];
  return allNames.map(name => {
    const prod = prodMap[name] || null;
    const draft = draftMap[name] || null;
    let status = "match", profileDiffs = [];
    if (prod && draft) {
      const capChanged = JSON.stringify(prod.adminCapabilityConfig || {}) !== JSON.stringify(draft.adminCapabilityConfig || {});
      const prodProfiles = prod.dataSecurityProfiles || [];
      const draftProfiles = draft.dataSecurityProfiles || [];
      const prodPMap = Object.fromEntries(prodProfiles.map(p => [p.analyticObjectId, p]));
      const draftPMap = Object.fromEntries(draftProfiles.map(p => [p.analyticObjectId, p]));
      const allObjIds = [...new Set([...Object.keys(prodPMap), ...Object.keys(draftPMap)])];
      profileDiffs = allObjIds.map(objId => {
        const pp = prodPMap[objId] || null;
        const dp = draftPMap[objId] || null;
        let pStatus = "match", dimDiffs = [];
        if (pp && dp) {
          const prodDims = flattenDimFilters(pp);
          const draftDims = flattenDimFilters(dp);
          const allDimIds = [...new Set([...Object.keys(prodDims), ...Object.keys(draftDims)])];
          dimDiffs = allDimIds.map(dimId => {
            const pd = prodDims[dimId] || null;
            const dd = draftDims[dimId] || null;
            let dStatus = "match";
            if (pd && !dd) dStatus = "prod_only";
            else if (!pd && dd) dStatus = "draft_only";
            else if (JSON.stringify(pd) !== JSON.stringify(dd)) dStatus = "mismatch";
            return { dimId, prod: pd, draft: dd, status: dStatus };
          }).sort((a, b) => {
            const o = { mismatch: 0, prod_only: 1, draft_only: 2, match: 3 };
            return (o[a.status] - o[b.status]) || a.dimId.localeCompare(b.dimId);
          });
          pStatus = dimDiffs.some(d => d.status !== "match") ? "mismatch" : "match";
        } else if (pp) {
          pStatus = "prod_only";
          const dims = flattenDimFilters(pp);
          dimDiffs = Object.entries(dims).map(([dimId, df]) => ({ dimId, prod: df, draft: null, status: "prod_only" }));
        } else {
          pStatus = "draft_only";
          const dims = flattenDimFilters(dp);
          dimDiffs = Object.entries(dims).map(([dimId, df]) => ({ dimId, prod: null, draft: df, status: "draft_only" }));
        }
        return { objId, prod: pp, draft: dp, status: pStatus, dimDiffs };
      });
      status = (profileDiffs.some(p => p.status !== "match") || capChanged) ? "mismatch" : "match";
    } else if (prod) {
      status = "prod_only";
      profileDiffs = (prod.dataSecurityProfiles || []).map(p => {
        const dims = flattenDimFilters(p);
        return { objId: p.analyticObjectId, prod: p, draft: null, status: "prod_only", dimDiffs: Object.entries(dims).map(([dimId, df]) => ({ dimId, prod: df, draft: null, status: "prod_only" })) };
      });
    } else {
      status = "draft_only";
      profileDiffs = (draft.dataSecurityProfiles || []).map(p => {
        const dims = flattenDimFilters(p);
        return { objId: p.analyticObjectId, prod: null, draft: p, status: "draft_only", dimDiffs: Object.entries(dims).map(([dimId, df]) => ({ dimId, prod: null, draft: df, status: "draft_only" })) };
      });
    }
    const diffCount = profileDiffs.filter(p => p.status !== "match").length;
    return { name, prod, draft, status, profileDiffs, diffCount };
  }).sort((a, b) => {
    const order = { mismatch: 0, prod_only: 1, draft_only: 2, match: 3 };
    const sd = order[a.status] - order[b.status];
    return sd !== 0 ? sd : a.name.localeCompare(b.name);
  });
}

// ── Shared components ──────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    match:      { label: "Match",      cls: "badge-match" },
    mismatch:   { label: "Mismatch",   cls: "badge-mismatch" },
    prod_only:  { label: "Prod only",  cls: "badge-prod" },
    draft_only: { label: "Draft only", cls: "badge-draft" },
  };
  const { label, cls } = map[status] || {};
  return <span className={`badge ${cls}`}>{label}</span>;
}

function StatCard({ value, label, color }) {
  return (
    <div className="stat-card">
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

// ── User Group row ─────────────────────────────────────────────────────────
function UserGroupRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = row.status !== "match";

  return (
    <>
      <div
        className={`diff-row diff-row-${row.status} ${canExpand ? "expandable" : ""}`}
        onClick={() => canExpand && setExpanded(e => !e)}
      >
        <div className="col-name">
          <span className="artifact-name">{row.name}</span>
          {row.status === "mismatch" && row.changedCount > 0 && (
            <span className="diff-count-badge">{row.changedCount} capability{row.changedCount !== 1 ? "s" : ""} changed</span>
          )}
          {canExpand && <span className="expand-hint">{expanded ? "▲ collapse" : "▼ expand"}</span>}
        </div>
        <div className="col-prod">
          {row.prod
            ? <span className="cell-present">{(row.prod.capabilities || []).length} capabilities</span>
            : <span className="cell-missing">— missing —</span>}
        </div>
        <div className="col-draft">
          {row.draft
            ? <span className="cell-present">{(row.draft.capabilities || []).length} capabilities</span>
            : <span className="cell-missing">— missing —</span>}
        </div>
        <div className="col-status"><StatusBadge status={row.status} /></div>
      </div>
      {expanded && (
        <div className="diff-expand">
          <div className="cap-table">
            <div className="cap-header">
              <div className="cap-col cap-col-name">Capability</div>
              <div className="cap-col cap-col-prod">Production</div>
              <div className="cap-col cap-col-draft">Draft</div>
              <div className="cap-col cap-col-status">Status</div>
            </div>
            {row.capDiffs.map(c => (
              <div key={c.cap} className={`cap-row cap-row-${c.status}`}>
                <div className="cap-col cap-col-name"><span className="cap-name">{c.cap}</span></div>
                <div className="cap-col cap-col-prod">
                  {c.inProd ? <span className="cap-yes">✓</span> : <span className="cap-no">—</span>}
                </div>
                <div className="cap-col cap-col-draft">
                  {c.inDraft ? <span className="cap-yes">✓</span> : <span className="cap-no">—</span>}
                </div>
                <div className="cap-col cap-col-status"><StatusBadge status={c.status} /></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function UserGroupsSection({ rows }) {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const counts = {
    total: rows.length,
    match: rows.filter(r => r.status === "match").length,
    mismatch: rows.filter(r => r.status === "mismatch").length,
    prodOnly: rows.filter(r => r.status === "prod_only").length,
    draftOnly: rows.filter(r => r.status === "draft_only").length,
  };
  const filtered = rows.filter(r => {
    const matchSearch = r.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filterStatus === "all" || r.status === filterStatus;
    return matchSearch && matchFilter;
  });
  return (
    <div className="artifact-section">
      <div className="section-header">
        <span className="section-icon">👥</span>
        <h2>User Groups</h2>
      </div>
      <div className="stats-row">
        <StatCard value={counts.total}     label="Total"      color="var(--text-primary)" />
        <StatCard value={counts.match}     label="Matching"   color="var(--green)" />
        <StatCard value={counts.mismatch}  label="Mismatch"   color="var(--amber)" />
        <StatCard value={counts.prodOnly}  label="Prod only"  color="var(--blue)" />
        <StatCard value={counts.draftOnly} label="Draft only" color="var(--teal)" />
      </div>
      <div className="filter-row">
        <input className="search-input" type="text" placeholder="Search by group name…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All ({counts.total})</option>
          <option value="mismatch">Mismatch ({counts.mismatch})</option>
          <option value="prod_only">Prod only ({counts.prodOnly})</option>
          <option value="draft_only">Draft only ({counts.draftOnly})</option>
          <option value="match">Match ({counts.match})</option>
        </select>
      </div>
      <div className="diff-table">
        <div className="diff-header">
          <div className="col-name">Group name</div>
          <div className="col-prod">Production</div>
          <div className="col-draft">Draft</div>
          <div className="col-status">Status</div>
        </div>
        {filtered.length === 0
          ? <div className="empty-state">No groups match the current filters.</div>
          : filtered.map(row => <UserGroupRow key={row.name} row={row} />)}
      </div>
    </div>
  );
}

// ── Permission components ──────────────────────────────────────────────────
function DimTable({ dimDiffs }) {
  return (
    <div className="dim-table">
      <div className="dim-header">
        <div className="dim-col dim-col-id">Dimension filter</div>
        <div className="dim-col dim-col-prod">Production</div>
        <div className="dim-col dim-col-draft">Draft</div>
        <div className="dim-col dim-col-status">Status</div>
      </div>
      {dimDiffs.map(dim => (
        <div key={dim.dimId} className={`dim-row dim-row-${dim.status}`}>
          <div className="dim-col dim-col-id"><span className="dim-id">{dim.dimId.replace("Employee.", "")}</span></div>
          <div className="dim-col dim-col-prod">{dim.prod ? <span className="dim-val">{getMemberSummary(dim.prod)}</span> : <span className="cell-missing">— missing —</span>}</div>
          <div className="dim-col dim-col-draft">{dim.draft ? <span className="dim-val">{getMemberSummary(dim.draft)}</span> : <span className="cell-missing">— missing —</span>}</div>
          <div className="dim-col dim-col-status"><StatusBadge status={dim.status} /></div>
        </div>
      ))}
    </div>
  );
}

function PermissionDetail({ row, dasLookup = {} }) {
  // Check if capabilities differ
  const prodCaps = row.prod?.adminCapabilityConfig?.capabilities || [];
  const draftCaps = row.draft?.adminCapabilityConfig?.capabilities || [];
  const prodCapsSet = new Set(prodCaps);
  const draftCapsSet = new Set(draftCaps);
  const allCaps = [...new Set([...prodCaps, ...draftCaps])].sort();
  const capDiffs = allCaps.map(cap => ({
    cap,
    inProd: prodCapsSet.has(cap),
    inDraft: draftCapsSet.has(cap),
    status: prodCapsSet.has(cap) && !draftCapsSet.has(cap) ? "prod_only"
          : !prodCapsSet.has(cap) && draftCapsSet.has(cap) ? "draft_only"
          : "match"
  })).filter(c => c.status !== "match");

  const hasProfileDiffs = row.profileDiffs && row.profileDiffs.length > 0;
  const hasCapDiffs = capDiffs.length > 0;

  return (
    <div className="diff-expand">
      {/* Capability differences */}
      {hasCapDiffs && (
        <div className="perm-profile-block">
          <div className="perm-profile-header">
            <span className="perm-profile-title">Capabilities</span>
            <StatusBadge status="mismatch" />
          </div>
          <div className="cap-table">
            <div className="cap-header">
              <div className="cap-col cap-col-name">Capability</div>
              <div className="cap-col cap-col-prod">Production</div>
              <div className="cap-col cap-col-draft">Draft</div>
              <div className="cap-col cap-col-status">Status</div>
            </div>
            {capDiffs.map(c => (
              <div key={c.cap} className={`cap-row cap-row-${c.status}`}>
                <div className="cap-col cap-col-name"><span className="cap-name">{c.cap}</span></div>
                <div className="cap-col cap-col-prod">{c.inProd ? <span className="cap-yes">✓</span> : <span className="cap-no">—</span>}</div>
                <div className="cap-col cap-col-draft">{c.inDraft ? <span className="cap-yes">✓</span> : <span className="cap-no">—</span>}</div>
                <div className="cap-col cap-col-status"><StatusBadge status={c.status} /></div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Data security profile differences */}
      {hasProfileDiffs && row.profileDiffs.map(profile => (
        <div key={profile.objId} className="perm-profile-block">
          <div className="perm-profile-header">
            <span className="perm-profile-title">{profile.objId}</span>
            <StatusBadge status={profile.status} />
          </div>
          {(profile.status === "draft_only" || profile.status === "prod_only") ? (
            <div className="profile-detail-summary">
              {(() => {
                const p = profile.prod || profile.draft;
                const side = profile.status === "draft_only" ? "Added in draft" : "Removed in draft";
                const dasId = p?.propertySetConfig?.shareableDataAccessSet?.dataAccessSetId;
                const allAccess = p?.allDataPointAccess;
                const hasFilters = p?.memberFilterConfigs?.length > 0;
                return (
                  <div className="profile-summary-rows">
                    <div className="profile-summary-row">
                      <span className="profile-summary-label">Change</span>
                      <span className={`profile-summary-val ${profile.status === "draft_only" ? "val-draft" : "val-prod"}`}>{side}</span>
                    </div>
                    {dasId && (
                      <div className="profile-summary-row">
                        <span className="profile-summary-label">Data Access Set</span>
                        <span className="profile-summary-val">{dasLookup[dasId] || dasId}</span>
                      </div>
                    )}
                    <div className="profile-summary-row">
                      <span className="profile-summary-label">Population Access</span>
                      <span className="profile-summary-val">{allAccess ? "All Access" : hasFilters ? "Filtered" : "No filters"}</span>
                    </div>
                    {hasFilters && profile.dimDiffs.length > 0 && <DimTable dimDiffs={profile.dimDiffs} />}
                  </div>
                );
              })()}
            </div>
          ) : (
            profile.dimDiffs.length > 0
              ? <DimTable dimDiffs={profile.dimDiffs} />
              : <div className="perm-profile-note">No dimension filters configured</div>
          )}
        </div>
      ))}
      {/* Nothing to show */}
      {!hasCapDiffs && !hasProfileDiffs && (
        <div className="perm-profile-note">No detail available for this difference.</div>
      )}
    </div>
  );
}

function PermissionRow({ row, dasLookup = {} }) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = row.status !== "match";
  return (
    <>
      <div
        className={`diff-row diff-row-${row.status} ${canExpand ? "expandable" : ""}`}
        onClick={() => canExpand && setExpanded(e => !e)}
      >
        <div className="col-name">
          <span className="artifact-name">{row.name}</span>
          {row.status === "mismatch" && row.diffCount > 0 && <span className="diff-count-badge">{row.diffCount} profile{row.diffCount !== 1 ? "s" : ""} differ</span>}
          {canExpand && <span className="expand-hint">{expanded ? "▲ collapse" : "▼ expand"}</span>}
        </div>
        <div className="col-prod">{row.prod ? <span className="cell-present">Present</span> : <span className="cell-missing">— missing —</span>}</div>
        <div className="col-draft">{row.draft ? <span className="cell-present">Present</span> : <span className="cell-missing">— missing —</span>}</div>
        <div className="col-status"><StatusBadge status={row.status} /></div>
      </div>
      {expanded && <PermissionDetail row={row} dasLookup={dasLookup} />}
    </>
  );
}

// ── DAS components ─────────────────────────────────────────────────────────
function getItemKey(item) {
  if (typeof item !== "object" || item === null) return JSON.stringify(item);
  for (const k of ["id", "permissionId", "propertyId", "dimensionId", "capabilityId", "analyticObjectId", "name", "displayName"]) {
    if (item[k] !== undefined) return `${k}:${item[k]}`;
  }
  return JSON.stringify(item);
}

function diffArrays(prodArr, draftArr) {
  const prodKeys = prodArr.map(getItemKey);
  const draftKeys = draftArr.map(getItemKey);
  const allKeys = [...new Set([...prodKeys, ...draftKeys])];
  return allKeys.map(key => {
    const prodItem = prodKeys.indexOf(key) >= 0 ? prodArr[prodKeys.indexOf(key)] : null;
    const draftItem = draftKeys.indexOf(key) >= 0 ? draftArr[draftKeys.indexOf(key)] : null;
    let status = "match";
    if (prodItem && !draftItem) status = "prod_only";
    else if (!prodItem && draftItem) status = "draft_only";
    else if (JSON.stringify(prodItem) !== JSON.stringify(draftItem)) status = "changed";
    return { key, prodItem, draftItem, status };
  });
}

function ArrayDiff({ prodArr, draftArr }) {
  const items = diffArrays(prodArr || [], draftArr || []);
  const visibleItems = items.filter(item => item.status !== "match");
  if (!visibleItems.length) return <div className="array-nodiff"><span className="array-nodiff-label">✓ identical ({prodArr.length} items)</span></div>;
  return (
    <div className="array-diff-wrap">
      <div className="array-diff-header">
        <div className="array-col array-col-item">Item</div>
        <div className="array-col array-col-prod">Production</div>
        <div className="array-col array-col-draft">Draft</div>
        <div className="array-col array-col-status">Status</div>
      </div>
      {visibleItems.map((item, i) => (
        <div key={i} className={`array-diff-row array-row-${item.status}`}>
          <div className="array-col array-col-item"><span className="array-item-key">{item.key.replace(/^[^:]+:/, "")}</span></div>
          <div className="array-col array-col-prod">{item.prodItem ? <span className="array-present">Present</span> : <span className="array-missing">— missing —</span>}</div>
          <div className="array-col array-col-draft">{item.draftItem ? <span className="array-present">Present</span> : <span className="array-missing">— missing —</span>}</div>
          <div className="array-col array-col-status">
            {item.status === "match"      && <span className="abadge abadge-match">Match</span>}
            {item.status === "prod_only"  && <span className="abadge abadge-prod">Prod only</span>}
            {item.status === "draft_only" && <span className="abadge abadge-draft">Draft only</span>}
            {item.status === "changed"    && <span className="abadge abadge-changed">Changed</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function DasRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = row.status !== "match";
  return (
    <>
      <div
        className={`diff-row diff-row-${row.status} ${canExpand ? "expandable" : ""}`}
        onClick={() => canExpand && setExpanded(e => !e)}
      >
        <div className="col-name">
          <span className="artifact-name">{row.name}</span>
          {row.status === "mismatch" && <span className="diff-count-badge">{row.diffKeys.length} field{row.diffKeys.length !== 1 ? "s" : ""} differ</span>}
          {canExpand && <span className="expand-hint">{expanded ? "▲ collapse" : "▼ expand"}</span>}
        </div>
        <div className="col-prod">{row.prod ? <span className="cell-present">Present</span> : <span className="cell-missing">— missing —</span>}</div>
        <div className="col-draft">{row.draft ? <span className="cell-present">Present</span> : <span className="cell-missing">— missing —</span>}</div>
        <div className="col-status"><StatusBadge status={row.status} /></div>
      </div>
      {expanded && (
        <div className="diff-expand">
          {row.status === "mismatch" && row.diffKeys.map(key => {
            const prodVal = row.prod?.[key];
            const draftVal = row.draft?.[key];
            const isArray = Array.isArray(prodVal) || Array.isArray(draftVal);
            return (
              <div key={key} className="diff-field-block">
                <div className="diff-field-block-header">
                  <span className="diff-field-block-key">{key}</span>
                  <span className="diff-field-block-tag">changed</span>
                </div>
                {isArray
                  ? <ArrayDiff prodArr={prodVal || []} draftArr={draftVal || []} />
                  : (
                    <div className="field-vals">
                      <div className="field-val-col">
                        <div className="field-val-label field-val-label-prod">Production</div>
                        <pre className="field-val-pre field-val-pre-prod">{JSON.stringify(prodVal, null, 2)}</pre>
                      </div>
                      <div className="field-val-col">
                        <div className="field-val-label field-val-label-draft">Draft</div>
                        <pre className="field-val-pre field-val-pre-draft">{JSON.stringify(draftVal, null, 2)}</pre>
                      </div>
                    </div>
                  )}
              </div>
            );
          })}
          {(row.status === "prod_only" || row.status === "draft_only") && (
            <pre className="solo-json">{JSON.stringify(row.prod || row.draft, null, 2)}</pre>
          )}
        </div>
      )}
    </>
  );
}

function getContentPackageArtifactSummary(artifact, artifactType) {
  if (!artifact) return "-";
  if (artifactType === "keyGroup") {
    const parts = [];
    if (artifact.canFilterBy !== undefined) parts.push(`Filter: ${artifact.canFilterBy ? "Yes" : "No"}`);
    if (artifact.canGroupBy !== undefined) parts.push(`Group: ${artifact.canGroupBy ? "Yes" : "No"}`);
    return parts.join(", ") || "Present";
  }
  return artifact.visible === undefined ? "Present" : artifact.visible ? "Visible" : "Not Visible";
}

function getGuidebookName(objectName) {
  const prefix = (objectName || "").split(".")[0];
  const suffix = "_Guidebook";
  if (!prefix.endsWith(suffix)) return null;
  return prefix.slice(0, -suffix.length).split("_").filter(Boolean).join(" ");
}

function ContentPackageArtifactTable({ artifactDiffs, artifactType }) {
  return (
    <div className="dim-table">
      <div className="dim-header">
        <div className="dim-col dim-col-id">Artifact</div>
        <div className="dim-col dim-col-prod">Production</div>
        <div className="dim-col dim-col-draft">Draft</div>
        <div className="dim-col dim-col-status">Status</div>
      </div>
      {artifactDiffs.map(artifact => {
        const guidebookName = artifactType === "guidebookTopic"
          ? getGuidebookName(artifact.draft?.objectName || artifact.prod?.objectName || artifact.key)
          : null;
        return (
        <div key={artifact.key} className={`dim-row dim-row-${artifact.status}`}>
          <div className="dim-col dim-col-id">
            <span className="dim-id">{artifact.prod?.displayName || artifact.draft?.displayName || artifact.key}</span>
            {guidebookName && <span className="dim-guide-label">Guide: {guidebookName}</span>}
          </div>
          <div className="dim-col dim-col-prod">
            {artifact.prod ? <span className="dim-val">{getContentPackageArtifactSummary(artifact.prod, artifactType)}</span> : <span className="cell-missing">- missing -</span>}
          </div>
          <div className="dim-col dim-col-draft">
            {artifact.draft ? <span className="dim-val">{getContentPackageArtifactSummary(artifact.draft, artifactType)}</span> : <span className="cell-missing">- missing -</span>}
          </div>
          <div className="dim-col dim-col-status"><StatusBadge status={artifact.status} /></div>
        </div>
        );
      })}
    </div>
  );
}

function ContentPackageDetail({ row }) {
  if (!row.artifactTypeDiffs.length) {
    return <div className="diff-expand"><div className="perm-profile-note">No visible artifacts in this package.</div></div>;
  }

  return (
    <div className="diff-expand">
      {row.artifactTypeDiffs.map(typeInfo => (
        <div key={typeInfo.artifactType} className="perm-profile-block">
          <div className="perm-profile-header">
            <span className="perm-profile-title">{typeInfo.artifactType}</span>
            <StatusBadge status={typeInfo.status} />
          </div>
          <ContentPackageArtifactTable artifactDiffs={typeInfo.artifactDiffs} artifactType={typeInfo.artifactType} />
        </div>
      ))}
    </div>
  );
}

function ContentPackageRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = row.status !== "match";
  return (
    <>
      <div
        className={`diff-row diff-row-${row.status} ${canExpand ? "expandable" : ""}`}
        onClick={() => canExpand && setExpanded(value => !value)}
      >
        <div className="col-name">
          <span className="artifact-name">{row.name}</span>
          {row.status === "mismatch" && row.diffCount > 0 && <span className="diff-count-badge">{row.diffCount} artifact type{row.diffCount !== 1 ? "s" : ""} differ</span>}
          {canExpand && <span className="expand-hint">{expanded ? "▲ collapse" : "▼ expand"}</span>}
        </div>
        <div className="col-prod">{row.prod ? <span className="cell-present">Present</span> : <span className="cell-missing">- missing -</span>}</div>
        <div className="col-draft">{row.draft ? <span className="cell-present">Present</span> : <span className="cell-missing">- missing -</span>}</div>
        <div className="col-status"><StatusBadge status={row.status} /></div>
      </div>
      {expanded && <ContentPackageDetail row={row} />}
    </>
  );
}

function DiffTable({ rows, search, filterStatus, type, dasLookup = {} }) {
  const filtered = rows.filter(r => {
    const matchSearch = r.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filterStatus === "all" || r.status === filterStatus;
    return matchSearch && matchFilter;
  });
  if (!filtered.length) return <div className="empty-state">No artifacts match the current filters.</div>;
  return (
    <div className="diff-table">
      <div className="diff-header">
        <div className="col-name">Artifact name</div>
        <div className="col-prod">Production</div>
        <div className="col-draft">Draft</div>
        <div className="col-status">Status</div>
      </div>
      {filtered.map(row =>
        type === "permissions"
          ? <PermissionRow key={row.name} row={row} dasLookup={dasLookup} />
          : type === "contentPackages"
            ? <ContentPackageRow key={row.name} row={row} />
            : <DasRow key={row.name} row={row} />
      )}
    </div>
  );
}

function ArtifactSection({ title, rows, icon, type, dasLookup = {} }) {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const counts = {
    total: rows.length,
    match: rows.filter(r => r.status === "match").length,
    mismatch: rows.filter(r => r.status === "mismatch").length,
    prodOnly: rows.filter(r => r.status === "prod_only").length,
    draftOnly: rows.filter(r => r.status === "draft_only").length,
  };
  return (
    <div className="artifact-section">
      <div className="section-header">
        <span className="section-icon">{icon}</span>
        <h2>{title}</h2>
      </div>
      <div className="stats-row">
        <StatCard value={counts.total}     label="Total"      color="var(--text-primary)" />
        <StatCard value={counts.match}     label="Matching"   color="var(--green)" />
        <StatCard value={counts.mismatch}  label="Mismatch"   color="var(--amber)" />
        <StatCard value={counts.prodOnly}  label="Prod only"  color="var(--blue)" />
        <StatCard value={counts.draftOnly} label="Draft only" color="var(--teal)" />
      </div>
      <div className="filter-row">
        <input className="search-input" type="text" placeholder="Search by name…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All ({counts.total})</option>
          <option value="mismatch">Mismatch ({counts.mismatch})</option>
          <option value="prod_only">Prod only ({counts.prodOnly})</option>
          <option value="draft_only">Draft only ({counts.draftOnly})</option>
          <option value="match">Match ({counts.match})</option>
        </select>
      </div>
      <DiffTable rows={rows} search={search} filterStatus={filterStatus} type={type} dasLookup={dasLookup} />
    </div>
  );
}

// ── Permission Comparator (original App) ───────────────────────────────────
function PermissionComparator() {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState(null);
  const [selectedProject, setSelectedProject] = useState("");
  const [comparing, setComparing] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [dasRows, setDasRows] = useState(null);
  const [psRows, setPsRows] = useState(null);
  const [ugRows, setUgRows] = useState(null);
  const [cpRows, setCpRows] = useState(null);
  const [cpLoading, setCpLoading] = useState(false);
  const [cpError, setCpError] = useState(null);
  const [dasLookup, setDasLookup] = useState({});
  const [dasLoading, setDasLoading] = useState(false);
  const [psLoading, setPsLoading] = useState(false);
  const [ugLoading, setUgLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("das");

  useEffect(() => {
    apiFetch("/projects")
      .then(data => { setProjects(data.projects || []); setProjectsLoading(false); })
      .catch(err => { setProjectsError(err.message); setProjectsLoading(false); });
  }, []);

  const handleCompare = useCallback(async () => {
    if (!selectedProject) return;
    setCompareError(null);
    setDasRows(null); setPsRows(null); setUgRows(null);
    setCpRows(null); setCpError(null); setCpLoading(true);
    setDasLoading(true); setPsLoading(false); setUgLoading(false);
    setComparing(true);
    setActiveTab("das");

    try {
      // Step 1 — Data Access Sets (show immediately when done)
      const prodDas  = await apiFetch("/artifacts/data-access-sets?projectId=production");
      const draftDas = await apiFetch(`/artifacts/data-access-sets?projectId=${selectedProject}`);
      // Build DAS ID → displayName lookup for permission detail view
      const lookup = {};
      [...(prodDas.dataAccessSets || []), ...(draftDas.dataAccessSets || [])].forEach(d => {
        if (d.id) lookup[d.id] = d.displayName || d.id;
      });
      setDasLookup(lookup);
      setDasRows(diffArtifacts(prodDas.dataAccessSets || [], draftDas.dataAccessSets || [], "displayName"));
      setDasLoading(false);
      setPsLoading(true);

      // Step 2 — Permission Sets
      const prodPs  = await apiFetch("/artifacts/permissions?projectId=production");
      const draftPs = await apiFetch(`/artifacts/permissions?projectId=${selectedProject}`);
      const prodPsErrors = Number(prodPs?.errorCount || 0);
      const draftPsErrors = Number(draftPs?.errorCount || 0);
      if (prodPsErrors > 0 || draftPsErrors > 0) {
        throw new Error(
          `Permission set details are incomplete (prod errors: ${prodPsErrors}, draft errors: ${draftPsErrors}). Please retry compare.`
        );
      }
      setPsRows(diffPermissions(prodPs.permissions || [], draftPs.permissions || []));
      setPsLoading(false);
      setUgLoading(true);

      // Step 3 — User Groups: fetch separately for prod and draft
      // Build capability map from prod permissions
      const prodCapMap = {};
      (prodPs.permissions || []).forEach(p => {
        prodCapMap[p.permissionId] = p.adminCapabilityConfig?.capabilities || [];
      });
      // Build capability map from draft permissions
      const draftCapMap = {};
      (draftPs.permissions || []).forEach(p => {
        draftCapMap[p.permissionId] = p.adminCapabilityConfig?.capabilities || [];
      });
      // Fetch user groups separately for prod and draft so membership differences are detected
      const ugProdData  = await apiFetch("/user-groups?projectId=production");
      const ugDraftData = await apiFetch(`/user-groups?projectId=${selectedProject}`);
      // Build prod user group capability lists using prod permission cap map
      const prodUgList = (ugProdData.userGroups || []).map(g => ({
        userGroupId: g.userGroupId,
        displayName: g.displayName,
        capabilities: [...new Set(
          (g.permissions || []).flatMap(p => prodCapMap[p.permissionId] || [])
        )].sort(),
      }));
      // Build draft user group capability lists using draft permission cap map
      const draftUgList = (ugDraftData.userGroups || []).map(g => ({
        userGroupId: g.userGroupId,
        displayName: g.displayName,
        capabilities: [...new Set(
          (g.permissions || []).flatMap(p => draftCapMap[p.permissionId] || prodCapMap[p.permissionId] || [])
        )].sort(),
      }));
      setUgRows(diffUserGroups(prodUgList, draftUgList));
      setUgLoading(false);

    } catch (err) {
      setCompareError(err.message);
      setDasLoading(false); setPsLoading(false); setUgLoading(false);
    } finally {
      try {
        const prodContentPackages = await apiFetch("/artifacts/content-packages?projectId=production");
        const draftContentPackages = await apiFetch(`/artifacts/content-packages?projectId=${selectedProject}`);
        setCpRows(diffContentPackages(
          prodContentPackages.contentPackages || [],
          draftContentPackages.contentPackages || [],
        ));
        const prodErrors = Number(prodContentPackages.errorCount || 0);
        const draftErrors = Number(draftContentPackages.errorCount || 0);
        if (prodErrors > 0 || draftErrors > 0) {
          setCpError(`Package details are incomplete (prod errors: ${prodErrors}, draft errors: ${draftErrors}).`);
        }
      } catch (err) {
        setCpRows([]);
        setCpError(err.message);
      } finally {
        setCpLoading(false);
        setComparing(false);
      }
    }
  }, [selectedProject]);

  const hasAnyResults = dasRows !== null || psRows !== null || ugRows !== null || cpRows !== null;
  const hasResults = dasRows !== null && psRows !== null && ugRows !== null && cpRows !== null;
  const totalMismatches = hasResults
    ? [...dasRows, ...psRows, ...ugRows, ...cpRows].filter(r => r.status !== "match").length
    : 0;

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-inner">
          <div className="logo">
            <div className="logo-mark logo-mark-route"><ToolIcon toolId="comparator" size={19} /></div>
            <div>
              <div className="logo-title">Permission Comparator</div>
              <div className="logo-sub">HRT Security &amp; Infrastructure · Production vs Draft</div>
            </div>
          </div>
          {hasResults && totalMismatches > 0 && (
            <div className="header-alert"><span className="alert-dot" />{totalMismatches} difference{totalMismatches !== 1 ? "s" : ""} found</div>
          )}
        </div>
      </header>

      <div className="control-bar">
        <div className="control-inner">
          <div className="control-group">
            <label className="control-label">Production</label>
            <div className="fixed-env prod-tag">Production (live)</div>
          </div>
          <div className="vs-divider">vs</div>
          <div className="control-group">
            <label className="control-label">Draft project</label>
            {projectsLoading ? <div className="loading-inline">Loading projects…</div>
              : projectsError ? <div className="error-inline">Error: {projectsError}</div>
              : (
                <select className="project-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
                  <option value="">— select a draft project —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.displayName || p.id}</option>)}
                </select>
              )}
          </div>
          <button className="compare-btn" disabled={!selectedProject || comparing} onClick={handleCompare}>
            {comparing ? <><span className="spinner" /> Comparing…</> : "Compare"}
          </button>
        </div>
      </div>

      <main className="main-content">
        {compareError && <div className="error-banner"><strong>Error:</strong> {compareError}</div>}

        {!hasAnyResults && !comparing && !compareError && (
          <div className="empty-hero">
            <div className="empty-icon">⟷</div>
            <div className="empty-title">Select a draft project to begin</div>
            <div className="empty-sub">Choose a draft project above and click Compare to see what's missing or different between production and your draft.</div>
          </div>
        )}

        {hasAnyResults && (
          <>
            <div className="tab-bar">
              <button className={`tab-btn ${activeTab === "das" ? "active" : ""}`} onClick={() => setActiveTab("das")}>
                Data Access Sets
                {dasLoading && <span className="tab-loading">loading…</span>}
                {dasRows && dasRows.filter(r => r.status !== "match").length > 0 && <span className="tab-badge">{dasRows.filter(r => r.status !== "match").length}</span>}
              </button>
              <button className={`tab-btn ${activeTab === "ps" ? "active" : ""}`} onClick={() => setActiveTab("ps")}>
                Permission Sets
                {psLoading && <span className="tab-loading">loading…</span>}
                {psRows && psRows.filter(r => r.status !== "match").length > 0 && <span className="tab-badge">{psRows.filter(r => r.status !== "match").length}</span>}
              </button>
              <button className={`tab-btn ${activeTab === "ug" ? "active" : ""}`} onClick={() => setActiveTab("ug")}>
                User Groups
                {ugLoading && <span className="tab-loading">loading…</span>}
                {ugRows && ugRows.filter(r => r.status !== "match").length > 0 && <span className="tab-badge">{ugRows.filter(r => r.status !== "match").length}</span>}
              </button>
              <button className={`tab-btn ${activeTab === "cp" ? "active" : ""}`} onClick={() => setActiveTab("cp")}>
                Content Packages
                {cpLoading && <span className="tab-loading">loading…</span>}
                {cpRows && cpRows.filter(r => r.status !== "match").length > 0 && <span className="tab-badge">{cpRows.filter(r => r.status !== "match").length}</span>}
              </button>
            </div>

            {activeTab === "das" && (
              dasRows
                ? <ArtifactSection title="Data Access Sets" rows={dasRows} icon="🔐" type="das" />
                : <div className="loading-state"><div className="loading-spinner" /><div>Loading Data Access Sets…</div></div>
            )}
            {activeTab === "ps" && (
              psRows
                ? <ArtifactSection title="Permission Sets" rows={psRows} icon="🛡️" type="permissions" dasLookup={dasLookup} />
                : <div className="loading-state"><div className="loading-spinner" /><div>Loading Permission Sets…</div></div>
            )}
            {activeTab === "ug" && (
              ugRows
                ? <UserGroupsSection rows={ugRows} />
                : <div className="loading-state"><div className="loading-spinner" /><div>Loading User Groups…</div></div>
            )}
            {activeTab === "cp" && (
              <>
                {cpError && <div className="error-banner"><strong>Content Packages:</strong> {cpError}</div>}
                {cpRows !== null
                  ? <ArtifactSection title="Content Packages" rows={cpRows} icon="📦" type="contentPackages" />
                  : <div className="loading-state"><div className="loading-spinner" /><div>Loading Content Packages…</div></div>}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ── CompareAccess — name helpers ──────────────────────────────────────────
// displayName is typically "Last, First" — split into two lines
function caNameParts(displayName) {
  if (!displayName) return { first: "", last: "" };
  const parts = displayName.split(",");
  if (parts.length >= 2) return { last: parts[0].trim(), first: parts.slice(1).join(",").trim() };
  // fallback: split on space
  const words = displayName.trim().split(" ");
  return words.length >= 2
    ? { first: words.slice(0, -1).join(" "), last: words[words.length - 1] }
    : { first: displayName, last: "" };
}

// ── CompareAccess — CSV export ────────────────────────────────────────────
function caDownloadCSV(rows, users) {
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const userCols = users.map((u, i) => u?.displayName || `Employee ${i + 1}`);
  const header = ["Access Profile", "Description", "Source", ...userCols].map(escape).join(",");
  const dataRows = rows.map(r => {
    const userVals = users.map((_, i) => r[`User${i + 1}`] || "");
    return [r.AccessProfile, r.Description, r.Source, ...userVals].map(escape).join(",");
  });
  const csv = [header, ...dataRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `compare-access-${Date.now()}.csv`; a.click();
  URL.revokeObjectURL(url);
}

// ── CompareAccess — build combined table from per-user access lists ───────
function caBuildCompareRows(userAccessLists, userMeta) {
  const allProfiles = new Set();
  userAccessLists.forEach(list => list.forEach(r => allProfiles.add(r.displayName)));
  return [...allProfiles].map(profile => {
    const row = { AccessProfile: profile };
    let description = "", source = "";
    userAccessLists.forEach((list, idx) => {
      const match = list.find(r => r.displayName === profile);
      row[`User${idx + 1}`] = match ? (match.revocable === "Requested" || match.revocable === true ? "Requested" : "Birthright") : null;
      if (match) {
        if (!description) description = match.description || "";
        if (!source)      source      = match.sourceName  || "";
      }
    });
    row.Description = description;
    row.Source = source;
    userMeta.forEach((u, i) => { row[`User${i + 1}Name`] = u?.displayName || ""; });
    return row;
  });
}

// ── CompareAccess — Employee name lookup panel ────────────────────────────
function CaEmployeeLookupPanel({ targetLabel, onSelect, onClose }) {
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [results,   setResults]   = useState(null);
  const [selected,  setSelected]  = useState(null);

  const handleSearch = async () => {
    if (!firstName.trim() && !lastName.trim()) { setError("Enter at least a first or last name."); return; }
    setLoading(true); setError(""); setResults(null); setSelected(null);
    try {
      const res  = await fetch(`${API_BASE}/employee-search`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `API error ${res.status}`);
      setResults(data.results || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleSearch(); };

  return (
    <div className="ca-lookup-panel">
      <div className="ca-lookup-header">
        <span className="ca-lookup-title">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          Search by name → populates <strong style={{ color: "var(--text-primary)" }}>{targetLabel}</strong>
        </span>
        <button className="ca-lookup-close" onClick={onClose}>✕</button>
      </div>
      <div className="ca-lookup-inputs">
        <div className="ca-lookup-field">
          <label className="control-label">First Name</label>
          <input autoFocus className="cug-input" placeholder="e.g. Jane"
            value={firstName} onChange={e => { setFirstName(e.target.value); setError(""); }}
            onKeyDown={handleKeyDown} disabled={loading} />
        </div>
        <div className="ca-lookup-field">
          <label className="control-label">Last Name</label>
          <input className="cug-input" placeholder="e.g. Smith"
            value={lastName} onChange={e => { setLastName(e.target.value); setError(""); }}
            onKeyDown={handleKeyDown} disabled={loading} />
        </div>
        <button className="compare-btn" onClick={handleSearch}
          disabled={loading || (!firstName.trim() && !lastName.trim())}
          style={{ alignSelf: "flex-end" }}>
          {loading ? <><span className="spinner" /> Searching…</> : "Search"}
        </button>
      </div>
      {error && <div className="ca-lookup-error">{error}</div>}
      {results !== null && (
        results.length === 0
          ? <div className="ca-lookup-empty">No employees found. Try different names.</div>
          : (
            <div className="ca-lookup-table-wrap">
              <div className="ca-lookup-thead">
                <div className="ca-lth">Employee ID</div>
                <div className="ca-lth">First Name</div>
                <div className="ca-lth">Last Name</div>
                <div className="ca-lth">Email</div>
              </div>
              <div className="ca-lookup-tbody">
                {results.map((r, i) => (
                  <div key={i}
                    className={`ca-lookup-tr ${selected === r.employeeNumber ? "ca-lookup-tr-selected" : ""}`}
                    onClick={() => { setSelected(r.employeeNumber); onSelect(r.employeeNumber); }}>
                    <div className="ca-ltd"><span className="ca-emp-id-badge">{r.employeeNumber || "—"}</span></div>
                    <div className="ca-ltd">{r.firstName || "—"}</div>
                    <div className="ca-ltd">{r.lastName  || "—"}</div>
                    <div className="ca-ltd ca-ltd-muted">{r.email || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )
      )}
    </div>
  );
}

// ── CompareAccess — Single employee slot ──────────────────────────────────
function CaEmpSlot({ index, value, onChange, onRemove, canRemove, disabled, color }) {
  const [showLookup, setShowLookup] = useState(false);
  const label = `Employee ${index + 1}`;
  return (
    <div className="ca-slot-wrap">
      <div className="ca-slot" style={{ "--slot-accent": color.accent, "--slot-dim": color.dim }}>
        <div className="ca-slot-header">
          <span className="ca-slot-label" style={{ color: color.accent }}>
            <span className="ca-slot-dot" style={{ background: color.accent }} />
            {label}
          </span>
          <div className="ca-slot-actions">
            <button
              className={`ca-lookup-toggle ${showLookup ? "ca-lookup-toggle-active" : ""}`}
              onClick={() => setShowLookup(v => !v)}
              disabled={disabled}
              style={showLookup ? { color: color.accent, borderColor: color.accent, background: color.dim } : {}}
              title="Search by name">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              </svg>
              {showLookup ? "Hide" : "Search by name"}
            </button>
            {canRemove && (
              <button className="ca-slot-remove" onClick={onRemove} disabled={disabled} title={`Remove ${label}`}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        <input
          className="cug-input ca-slot-input"
          placeholder={`Enter Employee ID ${index + 1}…`}
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
        />
      </div>
      {showLookup && (
        <div className="ca-slot-popover">
          <CaEmployeeLookupPanel
            targetLabel={label}
            onSelect={(id) => { onChange(id); setShowLookup(false); }}
            onClose={() => setShowLookup(false)}
          />
        </div>
      )}
    </div>
  );
}

// ── CompareAccess — Grant badge ───────────────────────────────────────────
function CaGrantBadge({ val }) {
  if (!val) return <span className="ca-cell-missing">—</span>;
  const cls = val === "Requested" ? "ca-grant-requested" : "ca-grant-birthright";
  return <span className={`ca-grant-badge ${cls}`}>{val}</span>;
}

// ── CompareAccess — Results table ─────────────────────────────────────────
function CaCompareTable({ rows, users, searchText, sourceFilter }) {
  const count = users.length;
  const filtered = rows.filter(r => {
    const matchSource = sourceFilter.length === 0 || sourceFilter.includes(r.Source);
    const q = searchText.toLowerCase();
    if (!q) return matchSource;
    return matchSource && (
      (r.AccessProfile || "").toLowerCase().includes(q) ||
      (r.Description || "").toLowerCase().includes(q) ||
      (r.Source        || "").toLowerCase().includes(q) ||
      users.some((_, i) => (r[`User${i + 1}`] || "").toLowerCase().includes(q))
    );
  });
  const sorted = [...filtered].sort((a, b) => (a.Source || "").localeCompare(b.Source || ""));
  // Profile + Source fixed, user cols share remaining space equally
  const cols = `minmax(180px, 1.2fr) minmax(150px, 1fr) ${users.map(() => "minmax(120px, 1fr)").join(" ")}`;

  return (
    <div className="ca-table-wrap">
      <div className="ca-thead" style={{ gridTemplateColumns: cols }}>
        <div className="ca-th">Access Profile</div>
        <div className="ca-th">Source</div>
        {users.map((u, i) => {
          const { first, last } = caNameParts(u?.displayName);
          return (
            <div key={i} className="ca-th ca-th-user"
              style={{ color: CA_USER_COLORS[i].accent, borderBottom: `2px solid ${CA_USER_COLORS[i].accent}` }}
              title={u?.displayName}>
              <span className="ca-th-user-dot" style={{ background: CA_USER_COLORS[i].accent }} />
              <span className="ca-th-user-name">
                <span className="ca-th-name-first">{first || u?.displayName || `Employee ${i + 1}`}</span>
                {last && <span className="ca-th-name-last">{last}</span>}
              </span>
            </div>
          );
        })}
      </div>
      <div className="ca-tbody">
        {sorted.length === 0
          ? <div className="ca-empty">No results match your current filters.</div>
          : sorted.map((r, i) => (
            <div key={i} className="ca-tr" style={{ gridTemplateColumns: cols }}>
              <div className="ca-td ca-td-profile" title={r.Description || r.AccessProfile || "—"}>{r.AccessProfile || "—"}</div>
              <div className="ca-td"><span className="as-source-badge">{r.Source || "—"}</span></div>
              {users.map((_, ui) => (
                <div key={ui} className="ca-td ca-td-grant"><CaGrantBadge val={r[`User${ui + 1}`]} /></div>
              ))}
            </div>
          ))}
      </div>
      <div className="ca-table-footer">
        Showing <strong style={{ color: "var(--text-primary)" }}>{sorted.length}</strong> of {rows.length} access profiles
      </div>
    </div>
  );
}

// ── CompareAccess — Main component ────────────────────────────────────────
function CompareAccess() {
  const [slotCount,    setSlotCount]    = useState(CA_MIN_USERS);
  const [empIds,       setEmpIds]       = useState(Array(CA_MAX_USERS).fill(""));
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [compareRows,  setCompareRows]  = useState(null);
  const [userMeta,     setUserMeta]     = useState([]);
  const [searchText,   setSearchText]   = useState("");
  const [sourceFilter, setSourceFilter] = useState([]);

  const setEmpId = (idx, val) => setEmpIds(prev => { const n = [...prev]; n[idx] = val; return n; });

  const addSlot = () => setSlotCount(c => Math.min(c + 1, CA_MAX_USERS));
  const removeSlot = (idx) => {
    setEmpIds(prev => { const n = [...prev]; n.splice(idx, 1); n.push(""); return n; });
    setSlotCount(c => Math.max(c - 1, CA_MIN_USERS));
  };

  const handleReset = () => {
    setEmpIds(Array(CA_MAX_USERS).fill(""));
    setSlotCount(CA_MIN_USERS);
    setCompareRows(null); setUserMeta([]);
    setError(""); setSearchText(""); setSourceFilter([]);
  };

  const handleCompare = useCallback(async () => {
    const activeIds = empIds.slice(0, slotCount).map(id => id.trim()).filter(Boolean);
    if (activeIds.length < 2) { setError("Please enter at least 2 Employee IDs to compare."); return; }
    setLoading(true); setError(""); setCompareRows(null); setUserMeta([]);
    setSearchText(""); setSourceFilter([]);
    try {
      const responses = await Promise.all(
        activeIds.map(id =>
          fetch(`${API_BASE}/access-search`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ employeeId: id }),
          }).then(r => r.json())
        )
      );
      const notFound = responses.map((d, i) => (!d.found ? activeIds[i] : null)).filter(Boolean);
      if (notFound.length > 0) {
        setError(`No user found for Employee ID(s): ${notFound.join(", ")}. Please verify and try again.`);
        return;
      }
      const userAccessLists = responses.map(d => (d.results || []).filter(r => r.type === "ACCESS_PROFILE" || !r.type));
      const meta = responses.map(d => d.userInfo || null);
      const rows = caBuildCompareRows(userAccessLists, meta);
      if (rows.length === 0) { setError("No access profiles found for any of the entered employees."); return; }
      setUserMeta(meta);
      setCompareRows(rows);
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, [empIds, slotCount]);

  const activeUsers = userMeta.filter(Boolean);
  const sources = compareRows ? [...new Set(compareRows.map(r => r.Source).filter(Boolean))].sort() : [];
  const toggleSource = useCallback(
    (s) => setSourceFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]), []
  );

  const handleExport = () => {
    if (!compareRows) return;
    const visible = compareRows.filter(r => {
      const matchSource = sourceFilter.length === 0 || sourceFilter.includes(r.Source);
      const q = searchText.toLowerCase();
      if (!q) return matchSource;
      return matchSource && (
        (r.AccessProfile || "").toLowerCase().includes(q) ||
        (r.Description || "").toLowerCase().includes(q) ||
        (r.Source        || "").toLowerCase().includes(q) ||
        activeUsers.some((_, i) => (r[`User${i + 1}`] || "").toLowerCase().includes(q))
      );
    });
    caDownloadCSV(visible, activeUsers);
  };

  return (
    <div className="ca-root">
      {/* Header */}
      <div className="ca-header">
        <div className="ca-header-inner">
          <div className="logo-mark logo-mark-route"><ToolIcon toolId="compare-access" size={19} /></div>
          <div>
            <div className="ca-title">Compare Access</div>
            <div className="ca-subtitle">Compare access profiles across up to {CA_MAX_USERS} employees side-by-side</div>
          </div>
        </div>
      </div>

      {/* Input panel */}
      <div className="ca-input-panel">
        <div className="ca-slots-grid">
          {Array.from({ length: slotCount }).map((_, i) => (
            <CaEmpSlot
              key={i} index={i} value={empIds[i]}
              onChange={(val) => setEmpId(i, val)}
              onRemove={() => removeSlot(i)}
              canRemove={slotCount > CA_MIN_USERS}
              disabled={loading}
              color={CA_USER_COLORS[i]}
            />
          ))}
          {slotCount < CA_MAX_USERS && (
            <button className="ca-add-slot" onClick={addSlot} disabled={loading}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Employee {slotCount + 1}
              <span className="ca-add-slot-hint">({CA_MAX_USERS - slotCount} more available)</span>
            </button>
          )}
        </div>
        <div className="ca-input-footer">
          <button className="compare-btn ca-compare-btn" onClick={handleCompare}
            disabled={loading || empIds.slice(0, slotCount).filter(id => id.trim()).length < 2}>
            {loading
              ? <><span className="spinner" /> Comparing…</>
              : <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                  </svg>
                  Compare Access
                </>}
          </button>
          {compareRows !== null && <button className="cug-reset-btn" onClick={handleReset}>Reset</button>}
          <span className="ca-slot-counter">
            {empIds.slice(0, slotCount).filter(id => id.trim()).length} / {slotCount} filled
          </span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="as-error-bar" style={{ margin: "16px 32px 0" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </div>
      )}

      {/* Info panel */}
      {compareRows === null && !loading && !error && (
        <div className="as-info-panel">
          <div className="as-info-title">How to use Compare Access</div>
          <div className="as-info-body">
            <p>Enter Employee IDs for 2–{CA_MAX_USERS} employees and click <strong style={{ color: "var(--green)" }}>Compare Access</strong> to see their profiles side-by-side.</p>
            <div className="as-info-features">
              {[
                ["👥", `Compare up to ${CA_MAX_USERS} employees simultaneously`],
                ["🔍", "Search and filter the results table instantly"],
                ["🏷",  "Filter by source application"],
                ["📋", "See Requested vs Birthright grant types per user"],
                ["🔎", "Don't know an ID? Use name search on each slot"],
                ["⬇",  "Export visible results to CSV"],
              ].map(([icon, text]) => (
                <div key={text} className="as-info-feature"><span>{icon}</span> {text}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {compareRows !== null && (
        <div className="ca-results">
          {/* User chips */}
          <div className="ca-user-bar">
            {activeUsers.map((u, i) => (
              <div key={i} className="ca-user-chip"
                style={{ borderColor: CA_USER_COLORS[i].accent, background: CA_USER_COLORS[i].dim }}>
                <span className="ca-user-avatar" style={{ background: CA_USER_COLORS[i].accent, color: "#0b1126" }}>
                  {(u?.displayName || "?").charAt(0).toUpperCase()}
                </span>
                <div className="ca-user-info-block">
                  <div className="ca-user-name" style={{ color: CA_USER_COLORS[i].accent }}>{u?.displayName || "—"}</div>
                  <div className="ca-user-email">{u?.email || "—"}</div>
                </div>
              </div>
            ))}
            <div className="ca-summary-pill">
              {compareRows.length} total profiles · {sources.length} source{sources.length !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Controls */}
          <div className="ca-controls">
            <input className="search-input" placeholder="Search profiles, sources, descriptions…"
              value={searchText} onChange={e => setSearchText(e.target.value)} />
            <button className="compare-btn" onClick={handleExport} style={{ padding: "0 16px" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export CSV
            </button>
          </div>

          {/* Source filter */}
          {sources.length > 1 && (
            <div className="ca-source-filter">
              <span className="ca-filter-label">Source:</span>
              <div className="ca-filter-pills">
                {sources.map(s => (
                  <button key={s} className={`ca-pill ${sourceFilter.includes(s) ? "ca-pill-active" : ""}`} onClick={() => toggleSource(s)}>{s}</button>
                ))}
              </div>
              {sourceFilter.length > 0 && <button className="ca-clear-btn" onClick={() => setSourceFilter([])}>Clear</button>}
            </div>
          )}

          <CaCompareTable
            rows={compareRows} users={activeUsers}
            searchText={searchText} sourceFilter={sourceFilter}
          />
        </div>
      )}

      <style>{`
        .ca-root { min-height: 100%; background: var(--bg-base); }
        .ca-header { background: var(--bg-surface); border-bottom: 1px solid var(--border); padding: 20px 32px; position: sticky; top: 0; z-index: 10; }
        .ca-header-inner { display: flex; align-items: center; gap: 14px; }
        .ca-title { font-size: 16px; font-weight: 600; color: var(--text-primary); }
        .ca-subtitle { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
        .ca-input-panel { background: transparent; border-bottom: 0; padding: 0 32px 24px; }
        .ca-slots-grid { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 20px; align-items: flex-start; }
        .ca-slot-wrap { position: relative; min-width: 260px; flex: 1 1 260px; max-width: 360px; }
        .ca-slot { background: var(--bg-raised); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; transition: border-color 0.2s; width: 100%; }
        .ca-slot:focus-within { border-color: var(--slot-accent, var(--green)); }
        .ca-slot-header { display: flex; align-items: center; justify-content: space-between; }
        .ca-slot-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; display: flex; align-items: center; gap: 6px; }
        .ca-slot-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
        .ca-slot-actions { display: flex; align-items: center; gap: 6px; }
        .ca-lookup-toggle { box-sizing: border-box; display: flex; align-items: center; gap: 5px; min-height: 38px; background: none; border: 1px solid var(--border-light); border-radius: var(--radius-md); color: var(--text-muted); font-size: 13px; font-family: var(--font-body); line-height: 18px; padding: 8px 12px; cursor: pointer; transition: color 0.15s, border-color 0.15s, background 0.15s; white-space: nowrap; }
        .ca-lookup-toggle:hover:not(:disabled) { color: var(--text-secondary); border-color: var(--text-muted); }
        .ca-lookup-toggle-active { color: var(--green) !important; border-color: var(--green) !important; background: var(--green-dim) !important; }
        .ca-slot-remove { display: flex; align-items: center; justify-content: center; background: none; border: 1px solid transparent; border-radius: var(--radius-sm); color: var(--text-muted); cursor: pointer; padding: 3px 5px; transition: color 0.15s, border-color 0.15s, background 0.15s; }
        .ca-slot-remove:hover:not(:disabled) { color: var(--red); border-color: var(--red); background: var(--red-dim); }
        .ca-slot-remove:disabled { opacity: 0.3; cursor: not-allowed; }
        .ca-slot-input { width: 100% !important; }
        .ca-slot-popover { position: absolute; top: calc(100% + 6px); left: 0; width: 480px; max-width: calc(100vw - 220px); z-index: 200; box-shadow: 0 8px 32px rgba(0,0,0,0.5); border-radius: var(--radius-lg); overflow: hidden; }
        .ca-add-slot { display: flex; align-items: center; gap: 8px; background: var(--bg-raised); border: 1px dashed var(--border-light); border-radius: var(--radius-lg); color: var(--text-muted); font-size: 13px; font-family: var(--font-body); font-weight: 500; padding: 14px 20px; cursor: pointer; transition: color 0.2s, border-color 0.2s, background 0.2s; min-width: 200px; flex: 0 0 auto; align-self: flex-start; }
        .ca-add-slot:hover:not(:disabled) { color: var(--green); border-color: var(--green); background: var(--green-dim); }
        .ca-add-slot:disabled { opacity: 0.3; cursor: not-allowed; }
        .ca-add-slot-hint { font-size: 10px; color: var(--text-muted); font-weight: 400; }
        .ca-input-footer { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .ca-compare-btn { font-size: 14px !important; padding: 0 24px !important; height: 40px !important; }
        .ca-slot-counter { font-size: 11px; color: var(--text-muted); margin-left: auto; }
        .ca-lookup-panel { background: var(--bg-base); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 14px; display: flex; flex-direction: column; gap: 12px; }
        .ca-lookup-header { display: flex; align-items: center; justify-content: space-between; }
        .ca-lookup-title { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-muted); font-style: italic; }
        .ca-lookup-close { background: none; border: none; color: var(--text-muted); cursor: pointer; font-size: 14px; line-height: 1; padding: 2px 6px; border-radius: var(--radius-sm); transition: color 0.15s, background 0.15s; }
        .ca-lookup-close:hover { color: var(--red); background: var(--red-dim); }
        .ca-lookup-inputs { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; }
        .ca-lookup-field { display: flex; flex-direction: column; gap: 6px; }
        .ca-lookup-error { font-size: 12px; color: var(--red); padding: 4px 0; }
        .ca-lookup-empty { font-size: 13px; color: var(--text-muted); padding: 8px 0; }
        .ca-lookup-table-wrap { border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; max-height: 260px; overflow-y: auto; width: 100%; }
        .ca-lookup-thead { display: grid; grid-template-columns: 110px 1fr 1fr 1.4fr; background: var(--bg-raised); border-bottom: 1px solid var(--border); position: sticky; top: 0; width: 100%; }
        .ca-lth { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); padding: 7px 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-lth:not(:last-child) { border-right: 1px solid var(--border); }
        .ca-lookup-tbody { display: flex; flex-direction: column; width: 100%; }
        .ca-lookup-tr { display: grid; grid-template-columns: 110px 1fr 1fr 1.4fr; border-bottom: 1px solid var(--border); cursor: pointer; transition: background 0.1s; width: 100%; }
        .ca-lookup-tr:last-child { border-bottom: none; }
        .ca-lookup-tr:hover { background: var(--bg-hover); }
        .ca-lookup-tr-selected { background: var(--green-dim) !important; }
        .ca-ltd { padding: 9px 10px; font-size: 12px; color: var(--text-secondary); display: flex; align-items: center; overflow: hidden; }
        .ca-ltd:not(:last-child) { border-right: 1px solid var(--border); }
        .ca-ltd-muted { font-size: 11px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-emp-id-badge { font-size: 10px; padding: 2px 7px; border-radius: var(--radius-sm); background: var(--green-dim); color: var(--green); border: 1px solid #1a4a28; font-weight: 600; }
        .ca-results { padding: 24px 32px 64px; display: flex; flex-direction: column; gap: 16px; }
        .ca-user-bar { display: flex; align-items: flex-start; gap: 10px; flex-wrap: wrap; }
        .ca-user-chip { display: flex; align-items: center; gap: 10px; border: 1px solid; border-radius: var(--radius-lg); padding: 10px 14px; min-width: 190px; }
        .ca-user-avatar { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; flex-shrink: 0; }
        .ca-user-info-block { flex: 1; min-width: 0; }
        .ca-user-name { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-user-email { font-size: 11px; color: var(--text-muted); margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-summary-pill { font-size: 11px; color: var(--text-muted); background: var(--bg-raised); border: 1px solid var(--border-light); border-radius: 999px; padding: 4px 12px; align-self: center; white-space: nowrap; }
        .ca-controls { display: flex; align-items: center; gap: 10px; }
        .ca-table-wrap { background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; overflow-x: auto; width: 100%; }
        .ca-thead { display: grid; background: var(--bg-raised); border-bottom: 1px solid var(--border); width: 100%; }
        .ca-th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); padding: 10px 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-th:not(:last-child) { border-right: 1px solid var(--border); }
        .ca-th-user { display: flex; align-items: center; gap: 6px; padding-bottom: 8px; }
        .ca-th-user-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
        .ca-th-user-name { display: flex; flex-direction: column; gap: 1px; min-width: 0; overflow: hidden; }
        .ca-th-name-first { font-size: 11px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-th-name-last { font-size: 10px; font-weight: 400; opacity: 0.7; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ca-tbody { width: 100%; }
        .ca-tr { display: grid; border-bottom: 1px solid var(--border); transition: background 0.1s; width: 100%; }
        .ca-tr:last-child { border-bottom: none; }
        .ca-tr:hover { background: var(--bg-hover); }
        .ca-td { padding: 10px 14px; font-size: 13px; color: var(--text-secondary); display: flex; align-items: center; }
        .ca-td:not(:last-child) { border-right: 1px solid var(--border); }
        .ca-td-profile { font-weight: 500; color: var(--text-primary); word-break: break-word; align-items: flex-start; padding-top: 12px; }
        .ca-td-grant { justify-content: center; }
        .ca-cell-missing { font-size: 11px; color: var(--text-muted); opacity: 0.4; }
        .ca-grant-badge { font-size: 10px; padding: 3px 9px; border-radius: 999px; font-weight: 500; white-space: nowrap; }
        .ca-grant-requested  { background: var(--green-dim); color: var(--green); border: 1px solid #1a4a28; }
        .ca-grant-birthright { background: var(--amber-dim); color: var(--amber); border: 1px solid #4a3600; }
        .ca-table-footer { padding: 10px 14px; font-size: 11px; color: var(--text-muted); border-top: 1px solid var(--border); background: var(--bg-raised); }
        .ca-empty { padding: 40px 24px; text-align: center; font-size: 13px; color: var(--text-muted); }
        .ca-source-filter { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .ca-filter-label { font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; white-space: nowrap; }
        .ca-filter-pills { display: flex; gap: 6px; flex-wrap: wrap; }
        .ca-pill { font-size: 11px; padding: 4px 12px; border-radius: 999px; border: 1px solid var(--border); background: var(--bg-raised); color: var(--text-muted); cursor: pointer; transition: all 0.15s; white-space: nowrap; font-family: var(--font-body); }
        .ca-pill:hover { border-color: var(--border-light); color: var(--text-secondary); background: var(--bg-hover); }
        .ca-pill-active { background: var(--blue-dim) !important; border-color: var(--blue) !important; color: var(--blue) !important; }
        .ca-clear-btn { font-size: 11px; padding: 4px 10px; border-radius: 999px; border: 1px solid var(--border); background: transparent; color: var(--text-muted); cursor: pointer; transition: color 0.15s; font-family: var(--font-body); }
        .ca-clear-btn:hover { color: var(--red); border-color: var(--red); }
      `}</style>
    </div>
  );
}

// ── Home / Landing Page ────────────────────────────────────────────────────
const HOME_LOGOS = [
  { name: "Infor", src: `${process.env.PUBLIC_URL || ""}/Logos/Infor.png`, href: "https://mingle-portal.inforcloudsuite.com/KOCHIND_PRD/" },
  { name: "SailPoint", src: `${process.env.PUBLIC_URL || ""}/Logos/Sailpoint.png`, href: "https://koch.identitynow.com/ui/d/dashboard" },
  { name: "Avature", src: `${process.env.PUBLIC_URL || ""}/Logos/Avature.png`, href: "https://koch.avature.net/#Home" },
  { name: "Tableau", src: `${process.env.PUBLIC_URL || ""}/Logos/Tableau.png`, href: "https://tableau.kochind.com/#/site/Koch-TalentAnalytics/home" },
  { name: "Visier", src: `${process.env.PUBLIC_URL || ""}/Logos/Visier.png`, href: "http://koch.visier.com/" },
];

const TEAM = [
  { name: "Young, Kim K", role: "Supervisor - App Security & Infrastructure (HRT)", initials: "KY", email: "kim.young@kochcc.com" },
  { name: "Finch, Dennis", role: "Application Security Architect", initials: "DF", email: "dennis.finch@kochcc.com" },
  { name: "Shah, Dhairya", role: "IT Product Owner", initials: "DS", email: "dhairya.shah@kochcc.com" },
  { name: "Thanneeru, Hemadri", role: "Kronos Engineer", initials: "HT", email: "hemadri.thanneeru@kochcc.com" },
  { name: "Marshall, John", role: "Technical Lead - HRT Infrastructure", initials: "JM", email: "john.marshall@kochcc.com" },
  { name: "Kalita, Rajkamal", role: "Sr. SAP Basis Administrator", initials: "RK", email: "rajkamal.kalita@kochcc.com" },
  { name: "Fatwa, Nabil", role: "HRIT Cloud Systems Analyst", initials: "NF", email: "nabil.fatwa@kochcc.com" },
  { name: "Singh, Sandhya", role: "SAP Basis Administrator", initials: "SS", email: "sandhya.singh1@kochcc.com" },
  { name: "Morrison, LaTanya R", role: "Application Security Analyst", initials: "LM", email: "latanya.morrison@kochcc.com" },
  { name: "Rajagopal, Yamini", role: "Technical Lead - Application Security", initials: "YR", email: "yamini.rajagopal@kochcc.com" },
  { name: "Barba, Cath", role: "Application Security Analyst", initials: "CB", email: "cath.barba@kochcc.com" },
  { name: "Mukherjee, Manish", role: "Application Security Analyst", initials: "MM", email: "manish.mukherjee@kochcc.com" },
  { name: "Chandru, Swathi", role: "Application Security Analyst", initials: "SC", email: "swathi.chandru@kochcc.com" },
  { name: "Naikode, Adarsh", role: "Associate - Application Security Analyst", initials: "AN", email: "adarsh.naikode@kochcc.com" },
  { name: "JALIYAL, VARSHA SHIVAYOGI", role: "Associate Application Security Analyst", initials: "JV", email: "varsha.jaliyal@kochcc.com" },
];

function HomePage({ onNavigate, allowedTools, previewGroupAccess }) {
  if (groupAccessEnabled || previewGroupAccess || process.env.REACT_APP_USE_LEGACY_PROD_HOME !== "true") {
    return <ProductionWorkspaceHome onNavigate={onNavigate} allowedTools={allowedTools} />;
  }

  return (
    <div className="home-root">
      <div className="home-header">
        <div className="home-header-left">
          <div className="logo-mark logo-mark-prod">H</div>
          <div>
            <div className="home-header-title-row">
              <span className="home-header-title">HRT Security &amp; Infrastructure Resource Center</span>
              <span className="env-badge env-badge-prod">
                <span className="env-badge-dot"></span>
                PRODUCTION
              </span>
            </div>
            <div className="home-header-sub">Production Environment · Koch</div>
          </div>
        </div>
      </div>

      <div className="home-body">
        <div className="home-main">
          <div className="home-welcome">
            <div className="home-welcome-title">Welcome to the HRT Security &amp; Infrastructure Resource Center!</div>
            <div className="home-welcome-desc">
              This site is your production hub for functionalities like Access Search, Access Request History, Security Access Report, Compare Access, User Group Management, and more. It also provides you with information on viewing supported applications, submitting Compass incidents, and getting team help.
            </div>
          </div>

          <div className="home-section-title">Quick Links</div>
          <div className="home-quick-links">
            <div className="home-card" onClick={() => onNavigate("entitlement-lookup")}>
              <div className="home-card-img">
                <div className="home-card-img-label">Entitlement Lookup</div>
                <div className="home-card-icon">🗂️</div>
              </div>
              <div className="home-card-label">Profile → Identities</div>
            </div>

            <div className="home-card" onClick={() => onNavigate("compare-access")}>
              <div className="home-card-img">
                <div className="home-card-img-label">Compare Access</div>
                <div className="home-card-icon">⚖️</div>
              </div>
              <div className="home-card-label">Compare Up to 5 Employees</div>
            </div>

            <div className="home-card" onClick={() => onNavigate("comparator")}>
              <div className="home-card-img">
                <div className="home-card-img-label">Permission Comparator</div>
                <div className="home-card-icon">🔐</div>
              </div>
              <div className="home-card-label">Compare Access</div>
            </div>

            <div className="home-card" onClick={() => onNavigate("access-search")}>
              <div className="home-card-img">
                <div className="home-card-img-label">Access Search</div>
                <div className="home-card-icon">🔍</div>
              </div>
              <div className="home-card-label">Search Employee Access</div>
            </div>

            <div className="home-card" onClick={() => onNavigate("access-request-history")}>
              <div className="home-card-img">
                <div className="home-card-img-label">Access Request History</div>
                <div className="home-card-icon">🕒</div>
              </div>
              <div className="home-card-label">Grant &amp; Revoke History</div>
            </div>

            <div className="home-card" onClick={() => onNavigate("security-access-report")}>
              <div className="home-card-img">
                <div className="home-card-img-label">Security Access Report</div>
                <div className="home-card-icon">📈</div>
              </div>
              <div className="home-card-label">myHR Security Access</div>
            </div>

            <div className="home-card" onClick={() => onNavigate("create-user-group")}>
              <div className="home-card-img">
                <div className="home-card-img-label">Visier Security Manager</div>
                <div className="home-card-icon">👥</div>
              </div>
              <div className="home-card-label">Manage Visier Security</div>
            </div>

            <div className="home-coming-soon">
              <div className="home-coming-soon-title">Coming soon…</div>
              <ul>
                <li>Generic Account Creation</li>
                <li>App Security Knowledge Base</li>
              </ul>
            </div>
          </div>

          <hr className="home-divider" />

          <div className="home-apps-section">
            <div className="home-section-title">Applications We Support</div>
            <div className="home-apps-logos">
              {HOME_LOGOS.map((app) => (
                <a
                  key={app.name}
                  href={app.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-app-logo-tile"
                  title={app.name}
                >
                  <img className="home-app-logo-image" src={app.src} alt={app.name} />
                </a>
              ))}
            </div>
          </div>

          <hr className="home-divider" />

          <div className="home-compass-section">
            <div className="home-section-title">Request Assistance in Compass</div>
            <div className="home-compass-body">
              <div className="home-compass-left">
                <p className="home-compass-instruction">
                  <u>Once you open the form in Compass, click the heart to save it to your favorites. Then enter the following:</u>
                </p>
                <p className="home-compass-question"><u>*What product or service do you need help with?</u></p>
                <ul className="home-compass-list">
                  <li><strong>myHR Infor Security</strong> (for myHR or Maximus Security topics)</li>
                  <li><strong>myHR Talent Analytics Security</strong> (for Tableau, QuickSight, or other Data &amp; Analytics Security topics)</li>
                  <li><strong>myHR Avature Security</strong> (for Avature or other Talent Admin security topics)</li>
                  <li><strong>KBS SAP HR Security</strong> (KGS SAP Environments)</li>
                  <li><strong>myHR ServiceNow HR Security</strong> (ServiceNow HR Service Delivery)</li>
                </ul>
                <p className="home-compass-note">
                  *<u>Assignment Group</u>: Will auto-populate to <strong>KGS HRT Application Security</strong> for any of the products listed above
                </p>
              </div>
              <div className="home-compass-right">
                <a
                  href="https://kochprod.service-now.com/compass?id=sc_cat_item&table=sc_cat_item&sys_id=3499c7d9dbd91300f37f7c28bf961901"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-compass-btn"
                >
                  <div className="home-compass-btn-title">Report An Issue</div>
                  <div className="home-compass-btn-desc">Report an Issue on something that is broken or incorrect.</div>
                </a>
                <a
                  href="https://kochprod.service-now.com/compass?id=sc_cat_item&table=sc_cat_item&sys_id=88a964b6db991b80f37f7c28bf96199f&searchTerm=submit%20a%20request"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-compass-btn"
                >
                  <div className="home-compass-btn-title">Submit A Request</div>
                  <div className="home-compass-btn-desc">Use this if you are requesting/asking something from the team.</div>
                </a>
              </div>
            </div>
          </div>

          <hr className="home-divider" />

          <div className="home-meet-title">Meet the Application Security Team</div>
          <div className="home-meet-desc">
            Our team ensures the security, stability, and compliance of both IT infrastructure and applications by managing security, overseeing application vulnerability assessments, and embedding secure coding practices into development. We lead incident response for infrastructure and application security issues, maintain patching for systems and applications, and enforce security policies to ensure compliance. Additionally, our team collaborates on cloud security architecture, automates security processes, and provides consulting, fostering a robust and comprehensive security posture for reliable and available infrastructure platforms across the organization.
          </div>
        </div>

        <div className="home-sidebar">
          <div className="home-sidebar-title">Our Team</div>
          <div className="home-team-list">
            {TEAM.map(member => (
              <div key={member.name} className="home-team-member">
                <div className="home-team-avatar">{member.initials}</div>
                <div className="home-team-info">
                  {member.email
                    ? <a href={`mailto:${member.email}`} className="home-team-name">{member.name}</a>
                    : <div className="home-team-name">{member.name}</div>}
                  <div className="home-team-role">{member.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Root App — sidebar navigation shell ───────────────────────────────────
const NAV = [
  {
    id: "home",
    label: "Home",
    sub: "Resource Center",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
  },
  {
    id: "comparator",
    label: "Permission Comparator",
    sub: "Production vs Draft",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  {
    id: "create-user-group",
    label: "Visier Security Manager",
    sub: "Non-Versioned Space",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <line x1="19" y1="8" x2="19" y2="14"/>
        <line x1="22" y1="11" x2="16" y2="11"/>
      </svg>
    ),
  },
  {
    id: "compare-access",
    label: "Compare Access",
    sub: "Up to 5 employees side-by-side",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  {
    id: "entitlement-lookup",
    label: "Entitlement Lookup",
    sub: "Profile → Assigned Identities",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    id: "access-search",
    label: "Access Search",
    sub: "Employee Access Profiles",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    ),
  },
  {
    id: "access-request-history",
    label: "Access Request History",
    sub: "Grant & Revoke History",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: "security-access-report",
    label: "Security Access Report",
    sub: "myHR Security Access",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <rect x="7" y="10" width="3" height="8" />
        <rect x="14" y="5" width="3" height="13" />
      </svg>
    ),
  },
  {
    id: "security-field-testing",
    label: "Security Field Testing",
    sub: "myHR Field Access Validation",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    ),
  },
  {
    id: "servicenow-actions",
    label: "ServiceNow Actions",
    sub: "Sprints & Story Management",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="8" y="2" width="8" height="4" rx="1" />
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        <path d="M12 11h4M12 16h4M8 11h.01M8 16h.01" />
      </svg>
    ),
  },
  {
    id: "sprint-metrics",
    label: "Sprint Metrics",
    sub: "Snapshots & unplanned work",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="M7 16v-3M12 16V9M17 16V5" />
      </svg>
    ),
  },
  {
    id: "analytics",
    label: "Usage & Analytics",
    sub: "User activity & statistics",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18M8 17v-3m5 3V5m5 12V9" />
      </svg>
    ),
  },
  {
    id: "feedback-inbox",
    label: "Feedback Inbox",
    sub: "Review submitted feedback",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
];

const PRODUCTION_ROUTE_IDS = new Set(NAV.filter((item) => item.id !== "home").map((item) => item.id));
const PRODUCTION_TOOLS = resourceTools.filter((tool) => PRODUCTION_ROUTE_IDS.has(tool.id));

export default function App({ profileName, profileEmail = "", profile = null, groupAccess = { superadmin: false, visitor: false }, previewGroupAccess = false, onSignOut }) {
  const [active, setActive] = useState("home");
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackNotice, setFeedbackNotice] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ Access: true });
  const isFeedbackAdmin = FEEDBACK_ADMIN_EMAILS.has(profileEmail.trim().toLowerCase());
  const canViewPage = (page) => page === "analytics"
    ? groupAccess.superadmin
    : !(groupAccessEnabled || previewGroupAccess) || canAccessPage(page, groupAccess);
  useEffect(() => {
    if (previewGroupAccess || IS_AUTH_PREVIEW || !profile) return;
    analytics.setUser(profile);
    return () => analytics.setUser(null);
  }, [profile, previewGroupAccess]);
  useEffect(() => {
    if (previewGroupAccess || IS_AUTH_PREVIEW || !profile) return;
    analytics.trackPageView(active);
  }, [active, profile, previewGroupAccess]);
  const visibleProductionTools = PRODUCTION_TOOLS.filter((tool) =>
    canViewPage(tool.id) && (tool.id !== "feedback-inbox" || isFeedbackAdmin));
  const visibleProductionGroups = [...new Set(visibleProductionTools.map((tool) => tool.group))];
  const activeTool = visibleProductionTools.find((tool) => tool.id === active);
  const navigate = (page) => {
    if (canViewPage(page) && (page !== "feedback-inbox" || isFeedbackAdmin)) {
      setActive(page);
    }
  };
  const activeNavItem = NAV.find((item) => item.id === active);
  const activePageTitle = active === "home" ? "HRTSI Resource Center" : activeNavItem?.label || "HRTSI Resource Center";
  const activePageSubtitle = active === "home"
    ? "Production Environment · Koch"
    : active === "create-user-group"
      ? "NonVersioned: true · Takes effect immediately · No new production version published"
      : activeNavItem?.sub || "";
  const toggleGroup = (group) => {
    if (collapsed) {
      setCollapsed(false);
      setExpandedGroups({ [group]: true });
      return;
    }
    setExpandedGroups((current) => ({ ...current, [group]: !current[group] }));
  };
  const toggleMobileNav = () => {
    setMobileNavOpen((open) => !open);
    setCollapsed(false);
  };
  const handleFeedbackSubmit = async (event) => {
    event.preventDefault();
    const message = feedbackMessage.trim();
    if (!message) {
      setFeedbackNotice("Enter a feedback message before submitting.");
      return;
    }
    if (IS_AUTH_PREVIEW || previewGroupAccess) {
      setFeedbackNotice("Submission is disabled in this local UI preview.");
      return;
    }
    setFeedbackSubmitting(true);
    setFeedbackNotice("");
    try {
      const response = await fetch(`${API_BASE}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "submit",
          message,
          pageUrl: `${window.location.origin}${window.location.pathname}`,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Feedback submission failed (${response.status}).`);
      }
      setFeedbackMessage("");
      setFeedbackNotice("Thank you. Your feedback has been submitted for review.");
    } catch (error) {
      setFeedbackNotice(error?.message || "Unable to submit feedback right now. Please try again.");
    } finally {
      setFeedbackSubmitting(false);
    }
  };
  const closeFeedback = () => {
    setFeedbackOpen(false);
    setFeedbackNotice("");
  };

  return (
    <div className={`root-shell active-${active}${mobileNavOpen ? " mobile-nav-open" : ""}${darkMode ? " theme-dark" : ""}`}>
      <ResourceSidebar production collapsed={collapsed} mobileOpen={mobileNavOpen} groups={visibleProductionGroups} tools={visibleProductionTools} expandedGroups={expandedGroups} activeGroup={activeTool?.group || "All tools"} selectedTool={activeTool?.name} onToggleCollapsed={() => setCollapsed((value) => !value)} onToggleGroup={toggleGroup} onSelectHome={() => { navigate("home"); setMobileNavOpen(false); }} onSelectGroup={() => {}} onSelectTool={(tool) => { navigate(tool.id); setMobileNavOpen(false); }} onFeedback={() => { setFeedbackMessage(""); setFeedbackNotice(""); setFeedbackOpen(true); setMobileNavOpen(false); }} onCloseMobile={() => setMobileNavOpen(false)} />

      {/* ── Main panel ── */}
      <div className="root-main">
        <header className="prod-global-header">
          <div className="prod-global-header-inner">
            <div className="prod-global-brand">
              <span className="prod-global-icon"><ToolIcon toolId={active} size={20} /></span>
              <span className="prod-global-page-copy"><span className="prod-global-title" aria-hidden={active === "feedback-inbox"}>{activePageTitle}</span><span className="prod-global-subtitle">{activePageSubtitle}</span></span>
            </div>
            <div className="prod-global-actions">
              <NavigationToggle open={mobileNavOpen} onToggle={toggleMobileNav} />
              {profileName && <div className="prod-global-session"><span className="prod-global-avatar" aria-hidden="true">{profileName.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase()}</span><span className="prod-global-session-identity"><small>Signed in</small><span>{profileName}</span></span>{onSignOut && <button type="button" onClick={onSignOut}>Sign out</button>}</div>}
              <button className="prod-theme-toggle" type="button" onClick={() => setDarkMode((value) => !value)} aria-label={`Switch to ${darkMode ? "light" : "dark"} mode`} aria-pressed={darkMode}>{darkMode ? <Sun size={15} /> : <Moon size={15} />}{darkMode ? "Light" : "Dark"}</button>
            </div>
          </div>
        </header>
        {active === "home"                && <HomePage onNavigate={navigate} allowedTools={visibleProductionTools.filter((tool) => tool.id !== "feedback-inbox")} previewGroupAccess={previewGroupAccess} />}
        {active === "comparator"          && <PermissionComparator />}
        {active === "compare-access"      && <CompareAccess />}
        {active === "create-user-group"   && <CreateUserGroup isSuperAdmin={groupAccess.superadmin} />}
        {active === "entitlement-lookup"  && <EntitlementLookup />}
        {active === "access-search"       && <AccessSearch />}
        {active === "access-request-history" && <AccessRequestHistory />}
        {active === "security-access-report" && <SecurityAccessReport />}
        {active === "security-field-testing" && canViewPage("security-field-testing") && <SecurityFieldTesting />}
        {active === "servicenow-actions" && canViewPage("servicenow-actions") && <ServiceNowActions theme={darkMode ? "dark" : "light"} />}
        {active === "sprint-metrics" && canViewPage("sprint-metrics") && <SprintMetrics />}
        {active === "analytics" && groupAccess.superadmin && <AnalyticsDashboard previewMode={previewGroupAccess || IS_AUTH_PREVIEW} />}
        {active === "feedback-inbox" && isFeedbackAdmin && <FeedbackInbox previewMode={IS_AUTH_PREVIEW || previewGroupAccess} />}
      </div>
      {feedbackOpen && <div className="feedback-preview-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) closeFeedback(); }}>
        <section className="feedback-preview-dialog" role="dialog" aria-modal="true" aria-labelledby="feedback-preview-title">
          <header className="feedback-preview-header">
            <h2 id="feedback-preview-title">Provide Feedback</h2>
            <button className="feedback-preview-icon-button" type="button" onClick={closeFeedback} disabled={feedbackSubmitting} aria-label="Close feedback dialog">×</button>
          </header>
          <form onSubmit={handleFeedbackSubmit}>
            <div className="feedback-preview-body">
              <p>Share suggestions, bugs, or improvements. Your feedback will be reviewed by the team responsible for the Resource Center.</p>
              <textarea
                autoFocus
                maxLength={1000}
                placeholder="Provide feedback"
                value={feedbackMessage}
                onChange={(event) => { setFeedbackMessage(event.target.value); setFeedbackNotice(""); }}
                disabled={feedbackSubmitting}
                aria-label="Provide feedback"
              />
              {feedbackNotice && <p className="feedback-preview-notice" role="status">{feedbackNotice}</p>}
            </div>
            <footer className="feedback-preview-footer">
              <button className="feedback-preview-close" type="button" onClick={closeFeedback} disabled={feedbackSubmitting}>Close</button>
              <button className="feedback-preview-submit" type="submit" disabled={feedbackSubmitting}>{feedbackSubmitting ? "Submitting…" : "Submit"}</button>
            </footer>
          </form>
        </section>
      </div>}
    </div>
  );
}
